import { appBackendApiClient } from './apiClient';

export type StreakStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type StreakMediaType = 'IMAGE' | 'VIDEO';
export type StreakMediaSource = 'UPLOAD' | 'URL';

export interface StreakAuthor {
  userId: string;
  displayName: string | null;
}

export interface AdminStreak {
  id: string;
  userId: string;
  title: string | null;
  caption: string | null;
  mediaUrl: string;
  mediaSource: StreakMediaSource;
  mediaType: StreakMediaType;
  mimeType: string | null;
  fileSize: number | null;
  durationSeconds: number | null;
  status: StreakStatus;
  rejectionReason: string | null;
  pointsAwarded: boolean;
  author: StreakAuthor;
  reviewedBy: StreakAuthor | null;
  reviewedAt: string | null;
  submittedAt: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApproveStreakResult {
  streak: AdminStreak;
  points: {
    status: 'awarded' | 'duplicate' | 'failed';
    pointsAwarded: number;
    message: string;
  };
}

export interface AdminListStreaksParams {
  status?: StreakStatus;
  userId?: string;
  includeDeleted?: boolean;
}

const streaksService = {
  async listStreaks(params?: AdminListStreaksParams): Promise<AdminStreak[]> {
    const { data } = await appBackendApiClient.get('/admin/streaks', { params });
    return data;
  },

  async getStreak(id: string): Promise<AdminStreak> {
    const { data } = await appBackendApiClient.get(`/admin/streaks/${id}`);
    return data;
  },

  async approveStreak(id: string): Promise<ApproveStreakResult> {
    const { data } = await appBackendApiClient.post(`/admin/streaks/${id}/approve`);
    return data;
  },

  async rejectStreak(id: string, reason?: string): Promise<AdminStreak> {
    const { data } = await appBackendApiClient.post(`/admin/streaks/${id}/reject`, {
      reason,
    });
    return data;
  },

  async updateStreak(
    id: string,
    payload: { title?: string; caption?: string },
  ): Promise<AdminStreak> {
    const { data } = await appBackendApiClient.patch(`/admin/streaks/${id}`, payload);
    return data;
  },

  async deleteStreak(id: string): Promise<void> {
    await appBackendApiClient.delete(`/admin/streaks/${id}`);
  },
};

export default streaksService;
